import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { H5PEditorUI } from '@lumieducation/h5p-react';
import { Container, Alert, Button, Spinner } from 'react-bootstrap';
import { ContentService } from '../services/ContentService';

interface EditPageProps {
    contentService: ContentService;
}

const EditPage: React.FC<EditPageProps> = ({ contentService }) => {
    const { contentId } = useParams<{ contentId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Get library from query params (URL decoding is handled automatically by useSearchParams)
    const library = searchParams.get('library') || undefined;
    const [editorModel, setEditorModel] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const h5pEditor = useRef<H5PEditorUI>(null);

    useEffect(() => {
        const loadContent = async () => {
            // Allow 'new' as a valid contentId, but require some value
            if (!contentId) {
                setError('Content ID is required');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                // Pass library from query params to getEdit
                // Library will be automatically URL-decoded by the browser
                console.log('Loading editor with contentId:', contentId, 'library:', library);
                const model = await contentService.getEdit(contentId, library);
                setEditorModel(model);
            } catch (err: any) {
                console.error('Error loading H5P editor:', err);
                setError(err.message || 'Failed to load H5P editor');
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, [contentId, library, contentService]);

    const handleSave = async () => {
        if (!h5pEditor.current || !contentId) {
            return;
        }

        try {
            setSaving(true);
            setSaveError(null);
            setSaveSuccess(false);

            await h5pEditor.current.save();
        } catch (err: any) {
            // Errors are handled in handleSaveError callback
            console.error('Error triggering save:', err);
            setSaving(false);
        }
    };

    const handleSaved = async (event: any) => {
        console.log('Content saved:', event);
        setSaving(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
    };

    const handleSaveError = async (event: any) => {
        console.error('Save error:', event);
        setSaving(false);
        setSaveError(event.detail?.message || 'Failed to save content');
        setTimeout(() => setSaveError(null), 5000);
    };

    const handleEditorLoaded = () => {
        console.log('H5P Editor loaded');
        setLoading(false);
    };

    if (loading) {
        return (
            <Container className="mt-5">
                <Alert variant="info">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading H5P editor...
                </Alert>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">
                    <Alert.Heading>Error</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button onClick={() => navigate('/')} variant="outline-danger">
                            Back to Home
                        </Button>
                    </div>
                </Alert>
            </Container>
        );
    }

    if (!editorModel) {
        return (
            <Container className="mt-5">
                <Alert variant="warning">No content available</Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-4">
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                    {saveSuccess && (
                        <Alert variant="success" className="d-inline-block mb-0 me-2 py-2">
                            Content saved successfully!
                        </Alert>
                    )}
                    {saveError && (
                        <Alert variant="danger" className="d-inline-block mb-0 me-2 py-2">
                            {saveError}
                        </Alert>
                    )}
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <React.Fragment>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Saving...
                            </React.Fragment>
                        ) : (
                            'Save'
                        )}
                    </Button>
                </div>
            </div>
            <div>
                <H5PEditorUI
                    ref={h5pEditor}
                    contentId={contentId || ''}
                    library={library} // Pass library prop from query params
                    loadContentCallback={async (cId: string) => {
                        // If contentId is 'new' and library is provided, pass it to getEdit
                        if (cId === 'new' && library) {
                            return await contentService.getEdit(cId, library);
                        }
                        return editorModel;
                    }}
                    saveContentCallback={async (cId: string, requestBody: any) => {
                        const result = await contentService.save(cId, requestBody);
                        return result;
                    }}
                    onSaved={handleSaved}
                    onSaveError={handleSaveError}
                    onLoaded={handleEditorLoaded}
                />
            </div>
        </Container>
    );
};

export default EditPage;
